import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ImageIcon,
  Loader2,
  Pencil,
  Plus,
  Sparkles,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { cn } from "@/lib/utils";
import { useBrands } from "../hooks/useBrands";
import { uploadBrandLogo } from "@/features/brand/services/brandLogoUpload";
import type { BrandConfig, BrandIdentity, ColorEntry } from "@/config/brand";
import {
  BRAND_TYPOGRAPHY_CATALOG,
  type BrandFontId,
} from "@/config/brandTypography";

// ── Static option data ────────────────────────────────────────────────

const STEP_LABELS = ["Brief", "Personality", "Look", "Done"] as const;
const TOTAL_INPUT_STEPS = 3; // Brief, Personality, Look (Done = read-only)

const TONE_CHIPS = [
  { id: "confident", label: "Confident" },
  { id: "playful", label: "Playful" },
  { id: "premium", label: "Premium" },
  { id: "technical", label: "Technical" },
  { id: "friendly", label: "Friendly" },
  { id: "bold", label: "Bold" },
  { id: "minimal", label: "Minimal" },
  { id: "warm", label: "Warm" },
  { id: "aspirational", label: "Aspirational" },
  { id: "quirky", label: "Quirky" },
  { id: "serious", label: "Serious" },
  { id: "direct", label: "Direct" },
] as const;

type PalettePreset = {
  id: string;
  name: string;
  colors: string[]; // 5 hex values
};

const PALETTE_PRESETS: PalettePreset[] = [
  { id: "editorial", name: "Editorial", colors: ["#0f172a", "#f97316", "#fef3c7", "#ffffff", "#94a3b8"] },
  { id: "botanical", name: "Botanical", colors: ["#1c4532", "#84a98c", "#f6e7c1", "#ffffff", "#9c2a1f"] },
  { id: "oceanic",   name: "Oceanic",   colors: ["#012a4a", "#01497c", "#a9d6e5", "#ffffff", "#fcbf49"] },
  { id: "sunlit",    name: "Sunlit",    colors: ["#3a2a1f", "#d6b89a", "#f4ede4", "#9c8568", "#ffffff"] },
];

const COLOR_ROLE_NAMES = ["Primary", "Secondary", "Accent", "Surface", "Muted"];

const DESCRIPTION_SAMPLES = [
  "We sell hand-roasted single-origin coffee, shipped within 48 hours of roast. Subscribers get one new origin every two weeks — never the same bag twice.",
  "Modern lighting for small spaces. Wireless, dimmable, clip-anywhere lamps designed by a former Apple ID lead. Sold direct, no retail markup.",
  "Wetsuits and beach essentials made from recycled neoprene. Built for cold-water surfers in the Pacific Northwest who want gear that lasts a decade.",
];

const PRIMARY_GRADIENT = "linear-gradient(90deg, #0053dc, #3e76fe)";

// ── Helpers ───────────────────────────────────────────────────────────

function brandInitials(name: string): string {
  const parts = (name || "New brand").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "NB";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function paletteToColorEntries(colors: string[]): ColorEntry[] {
  return colors.map((hex, i) => ({
    name: COLOR_ROLE_NAMES[i] ?? `Color ${i + 1}`,
    hex,
  }));
}

// ── Form state ────────────────────────────────────────────────────────

interface FormState {
  name: string;
  website: string;
  description: string;
  tones: string[];
  tagline: string;
  values: string;
  paletteId: string;
  paletteColors: string[];
  customColors: ColorEntry[]; // used when "Build a palette from scratch" is active
  useCustomPalette: boolean;
  headingFontId: BrandFontId;
  bodyFontId: BrandFontId;
  /** Optional CDN URL for the brand logo. Captured in the Look step and
   *  persisted as `assets.logo`; downstream image-ad dispatch (`useAdDispatch`)
   *  forwards it as the LAST `reference_image_urls` entry so generated ads
   *  carry the user's official mark instead of the reference template's. */
  logoUrl: string | null;
}

const INITIAL_STATE: FormState = {
  name: "",
  website: "",
  description: "",
  tones: [],
  tagline: "",
  values: "",
  paletteId: PALETTE_PRESETS[0].id,
  paletteColors: PALETTE_PRESETS[0].colors,
  customColors: [
    { name: "Primary", hex: "#0f172a" },
    { name: "Accent", hex: "#0053dc" },
    { name: "Surface", hex: "#ffffff" },
  ],
  useCustomPalette: false,
  headingFontId: "inter",
  bodyFontId: "inter",
  logoUrl: null,
};

// ── Public component ──────────────────────────────────────────────────

export function ManualBrandDna({
  workspaceId,
  onBack,
}: {
  workspaceId?: string;
  onBack: () => void;
}) {
  const { createBrand, setActiveBrand } = useBrands();
  const navigate = useNavigate();

  const [step, setStep] = useState(0); // 0..2 for input steps; 3 = done

  const [state, setStateRaw] = useState<FormState>(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdBrand, setCreatedBrand] = useState<BrandIdentity | null>(null);

  const set = (patch: Partial<FormState>) =>
    setStateRaw((prev) => ({ ...prev, ...patch }));

  const effectivePalette = state.useCustomPalette
    ? state.customColors.map((c) => c.hex)
    : state.paletteColors;

  const stepValid: Array<() => boolean> = [
    () => state.name.trim().length > 0 && state.description.trim().length >= 30,
    () => state.tones.length > 0,
    () =>
      state.useCustomPalette
        ? state.customColors.length > 0 &&
          state.customColors.every((c) => /^#[0-9a-f]{3,8}$/i.test(c.hex.trim()))
        : !!state.paletteId,
  ];

  const canAdvance = step < TOTAL_INPUT_STEPS && stepValid[step]();
  const isDone = step === TOTAL_INPUT_STEPS;
  const isLastInputStep = step === TOTAL_INPUT_STEPS - 1;

  const goNext = () => {
    if (canAdvance) setStep((s) => Math.min(s + 1, TOTAL_INPUT_STEPS));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));
  const goSkip = () => {
    // Personality is the only optional step; allow skipping forward.
    if (step > 0 && step < TOTAL_INPUT_STEPS - 1) setStep((s) => s + 1);
  };

  const handleClose = () => {
    if (submitting) return;
    if (createdBrand) {
      onBack();
      return;
    }
    if (
      state.name ||
      state.description ||
      state.tones.length ||
      state.tagline ||
      state.values
    ) {
      if (!window.confirm("Discard this brand?")) return;
    }
    onBack();
  };

  const handleCreate = async () => {
    if (!stepValid[0]() || !stepValid[2]()) {
      setError("Some required fields are missing.");
      return;
    }
    setSubmitting(true);
    setError(null);

    const colorPalette = state.useCustomPalette
      ? state.customColors
          .map((c) => ({ name: c.name.trim() || "Color", hex: c.hex.trim() }))
          .filter((c) => /^#[0-9a-f]{3,8}$/i.test(c.hex))
      : paletteToColorEntries(state.paletteColors);

    const toneLabels = state.tones
      .map((id) => TONE_CHIPS.find((t) => t.id === id)?.label)
      .filter((l): l is NonNullable<typeof l> => Boolean(l));

    const valuesList = state.values
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    const cleanWebsite = state.website.trim().replace(/^https?:\/\//i, "");

    const payload: BrandConfig = {
      identity: {
        id: "",
        name: state.name.trim(),
        description: state.description.trim(),
        genre: "",
        styleReference: "",
        createdAt: "",
        updatedAt: "",
      },
      assets: state.logoUrl ? { logo: state.logoUrl } : {},
      visuals: {
        fidelityKeywords: "",
        compositingRule: "",
        colorPalette,
        aspectRatio: "16:9",
        typography: {
          headingFontId: state.headingFontId,
          bodyFontId: state.bodyFontId,
        },
      },
      directingRules: {
        thirtyDegreeRule: false,
        establishingShotProtocol: false,
        sixLayerFramework: false,
        customRules: [],
      },
      characters: [],
      voiceMap: [],
      worldBible: "",
      strategy: {
        website: cleanWebsite ? `https://${cleanWebsite}` : undefined,
        brandTagline: state.tagline.trim() || undefined,
        toneOfVoice: toneLabels.length ? toneLabels.join(", ") : undefined,
        suggestedTone: toneLabels.length ? toneLabels.join(", ") : undefined,
        uniqueValueProposition: valuesList.length ? valuesList.join(", ") : undefined,
      },
      contextDictionary: {
        ...(workspaceId ? { workspaceId } : {}),
      },
    };

    try {
      const created = await createBrand(payload);
      await setActiveBrand(created.id);
      setCreatedBrand(created);
      setStep(TOTAL_INPUT_STEPS);
    } catch (err: any) {
      setError(err?.message ?? "Failed to create brand");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpen = () => {
    if (createdBrand) navigate(`/brands/${createdBrand.id}`);
  };

  return (
    <div className="flex min-h-full flex-col bg-background">
      <TopBar
        step={step}
        name={state.name}
        palette={effectivePalette}
        onClose={handleClose}
        setStep={setStep}
      />

      <div className="mx-auto w-full max-w-[1080px] flex-1 px-7 pb-24 pt-12">
        {error && (
          <div className="mb-6 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 ds-body-sm text-destructive">
            {error}
          </div>
        )}

        {step === 0 && <StepBrief state={state} set={set} />}
        {step === 1 && <StepPersonality state={state} set={set} />}
        {step === 2 && <StepLook state={state} set={set} />}
        {step === 3 && createdBrand && (
          <StepDone
            state={state}
            palette={effectivePalette}
            onOpen={handleOpen}
            onEdit={handleOpen}
          />
        )}
      </div>

      {!isDone && (
        <FooterNav
          step={step}
          canAdvance={!!canAdvance}
          submitting={submitting}
          isLast={isLastInputStep}
          onBack={goBack}
          onNext={goNext}
          onSkip={goSkip}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

export default ManualBrandDna;

// ── Top progress bar ──────────────────────────────────────────────────

function TopBar({
  step,
  name,
  palette,
  onClose,
  setStep,
}: {
  step: number;
  name: string;
  palette: string[];
  onClose: () => void;
  setStep: (s: number) => void;
}) {
  return (
    <div className="sticky top-0 z-20 flex items-center gap-6 border-b border-border bg-background/85 px-7 py-3 backdrop-blur">
      <button
        type="button"
        onClick={onClose}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13px] font-semibold text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Brands
      </button>

      <div className="mx-auto flex max-w-[560px] flex-1 items-center gap-2.5">
        {STEP_LABELS.map((label, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <React.Fragment key={label}>
              <button
                type="button"
                onClick={() => setStep(i)}
                className={cn(
                  "flex shrink-0 items-center gap-2",
                  "cursor-pointer"
                )}
              >
                <div
                  className={cn(
                    "flex h-[22px] w-[22px] items-center justify-center rounded-full ds-micro-strong",
                    done && "text-white",
                    active && "border-2 border-primary bg-card text-primary",
                    !done && !active && "bg-muted text-muted-foreground",
                  )}
                  style={done ? { background: PRIMARY_GRADIENT } : undefined}
                >
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </div>
                <span
                  className={cn(
                    "whitespace-nowrap text-xs",
                    active && "font-bold text-foreground",
                    done && "font-semibold text-foreground",
                    !active && !done && "font-medium text-muted-foreground",
                  )}
                >
                  {label}
                </span>
              </button>
              {i < STEP_LABELS.length - 1 && (
                <div
                  className={cn(
                    "h-0.5 min-w-[20px] flex-1 rounded",
                    i < step ? "bg-primary" : "bg-border",
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="flex shrink-0 items-center gap-2.5">
        <div className="min-w-0 text-right">
          <div className="whitespace-nowrap ds-micro-strong uppercase text-muted-foreground">
            Live preview
          </div>
          <div className="max-w-[160px] truncate text-[13px] font-bold text-foreground">
            {name || "New brand"}
          </div>
        </div>
        <MiniMonogram name={name} palette={palette} />
      </div>
    </div>
  );
}

function MiniMonogram({ name, palette }: { name: string; palette: string[] }) {
  const initials = brandInitials(name);
  const [bg = "#0f172a", accent = "#ffffff"] = palette;
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-[15px] font-black"
      style={{ background: bg, color: accent, letterSpacing: "-0.02em" }}
    >
      {initials}
    </div>
  );
}

function LivePreview({ name, palette }: { name: string; palette: string[] }) {
  const initials = brandInitials(name);
  const [bg = "#0f172a", accent = "#ffffff"] = palette;
  return (
    <div
      className="flex h-24 w-24 shrink-0 items-center justify-center rounded-[20px] text-4xl font-black shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06),0_8px_32px_rgba(48,50,59,0.06)]"
      style={{ background: bg, color: accent, letterSpacing: "-0.03em" }}
    >
      {initials}
    </div>
  );
}

// ── Step header ───────────────────────────────────────────────────────

function StepHeader({
  step,
  eyebrow,
  title,
  sub,
}: {
  step: number;
  eyebrow: string;
  title: string;
  sub?: string;
}) {
  return (
    <div className="mb-8">
      <div className="mb-3.5 flex flex-wrap items-center gap-3">
        <div className="whitespace-nowrap ds-micro-strong uppercase text-primary">
          {eyebrow}
        </div>
        <span className="whitespace-nowrap ds-micro-strong uppercase text-muted-foreground">
          Step {step} of {TOTAL_INPUT_STEPS}
        </span>
      </div>
      <h1
        className="text-[28px] sm:text-[42px] font-black leading-[1.04] text-foreground"
        style={{ letterSpacing: "-0.03em" }}
      >
        {title}
      </h1>
      {sub && (
        <p className="mt-2.5 w-full max-w-[600px] text-[15px] font-medium leading-[1.55] text-muted-foreground">
          {sub}
        </p>
      )}
    </div>
  );
}

// ── Field primitives ──────────────────────────────────────────────────

function FieldLabel({
  children,
  required,
  hint,
}: {
  children: React.ReactNode;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <label className="text-[13px] font-bold text-foreground">
        {children}
        {required && <span className="ml-1 text-primary">*</span>}
      </label>
      {hint && (
        <span className="max-w-[70%] text-right ds-micro text-muted-foreground">
          {hint}
        </span>
      )}
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <FieldLabel required={required} hint={hint}>
        {label}
      </FieldLabel>
      {children}
    </div>
  );
}

const inputCls =
  "h-11 w-full rounded-xl border border-border bg-card px-3.5 text-[15px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20";

const inputBigCls = inputCls; // identical heights — kept for parity with design tokens
const textareaCls =
  "w-full rounded-xl border border-border bg-card p-3.5 text-[15px] font-medium leading-[1.55] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 resize-y";

// ── Step 1 — Brief ────────────────────────────────────────────────────

function StepBrief({
  state,
  set,
}: {
  state: FormState;
  set: (patch: Partial<FormState>) => void;
}) {
  return (
    <>
      <StepHeader
        step={1}
        eyebrow="The Brief"
        title="What are we building?"
        sub="Use this brief to draft palette, voice and identity. Be specific — the more concrete the description, the sharper the DNA."
      />
      <div className="grid max-w-[720px] grid-cols-1 gap-5">
        <Field label="Brand name" required>
          <input
            value={state.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="e.g. Eleven & Co."
            autoFocus
            className={inputBigCls}
          />
        </Field>

        <Field
          label="Website"
          hint="Optional — we'll auto-fetch logo & favicon if you add it."
        >
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] font-medium text-muted-foreground">
              https://
            </span>
            <input
              value={state.website.replace(/^https?:\/\//i, "")}
              onChange={(e) => set({ website: e.target.value })}
              placeholder="elevenandco.com"
              className={cn(inputCls, "pl-[68px]")}
            />
          </div>
        </Field>

        <Field
          label="What does your brand do, and what makes it unique?"
          required
          hint="One paragraph is enough. Describe what you sell, who it's for, and the angle that makes you different."
        >
          <textarea
            value={state.description}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="e.g. Eleven & Co. makes performance running apparel for women who train every day. Cut from technical fabrics in colorways borrowed from desert sunsets — built for the long, slow miles, not the race-day photo."
            rows={6}
            className={textareaCls}
          />
          <div className="mt-1.5 flex items-center justify-between ds-micro text-muted-foreground">
            <span>{state.description.length} characters</span>
            <button
              type="button"
              onClick={() =>
                set({
                  description:
                    DESCRIPTION_SAMPLES[
                      Math.floor(Math.random() * DESCRIPTION_SAMPLES.length)
                    ],
                })
              }
              className="inline-flex items-center gap-1 ds-micro-strong text-primary hover:underline"
            >
              <Sparkles className="h-3 w-3" /> Need an example?
            </button>
          </div>
        </Field>
      </div>
    </>
  );
}

// ── Step 2 — Personality ──────────────────────────────────────────────

function StepPersonality({
  state,
  set,
}: {
  state: FormState;
  set: (patch: Partial<FormState>) => void;
}) {
  const toggleTone = (id: string) => {
    const has = state.tones.includes(id);
    const next = has
      ? state.tones.filter((t) => t !== id)
      : [...state.tones, id].slice(-3); // cap at 3
    set({ tones: next });
  };

  return (
    <>
      <StepHeader
        step={2}
        eyebrow="Personality"
        title="How should it sound?"
        sub="Pick up to three tone words. They become the voice every Co-Agent uses when writing ads, product copy, and emails."
      />
      <div className="flex max-w-[780px] flex-col gap-7">
        <div>
          <FieldLabel>Tone of voice</FieldLabel>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {TONE_CHIPS.map((c) => {
              const selected = state.tones.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleTone(c.id)}
                  className={cn(
                    "inline-flex h-9 items-center rounded-full border px-3.5 text-[13px] transition-colors",
                    selected
                      ? "border-primary bg-primary/[0.06] font-bold text-primary"
                      : "border-border bg-card font-medium text-foreground hover:bg-muted/40",
                  )}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="mt-2 ds-micro text-muted-foreground">
            {state.tones.length}/3 selected
          </div>
        </div>

        <Field
          label="Tagline"
          hint="Optional. The one-liner that captures it."
        >
          <input
            value={state.tagline}
            onChange={(e) => set({ tagline: e.target.value })}
            placeholder="Run further. Run lighter."
            className={inputBigCls}
          />
        </Field>

        <Field
          label="What you stand for"
          hint="Optional. Comma-separated values — these will be woven into your copy."
        >
          <input
            value={state.values}
            onChange={(e) => set({ values: e.target.value })}
            placeholder="Durability, transparency, craft"
            className={inputBigCls}
          />
        </Field>
      </div>
    </>
  );
}

// ── Step 3 — Look ─────────────────────────────────────────────────────

function StepLook({
  state,
  set,
}: {
  state: FormState;
  set: (patch: Partial<FormState>) => void;
}) {
  return (
    <>
      <StepHeader
        step={3}
        eyebrow="The Look"
        title="Pick a palette."
        sub="We've drafted these from your brief. You can refine each color once you've picked one."
      />

      <div className="mb-8 max-w-[880px]">
        <FieldLabel hint="Optional. Generated ads will use this exact mark instead of inventing one or copying the reference image's logo.">
          Logo
        </FieldLabel>
        <LogoUploader
          logoUrl={state.logoUrl}
          onChange={(url) => set({ logoUrl: url })}
        />
      </div>

      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <FieldLabel>Color palette</FieldLabel>
          <button
            type="button"
            onClick={() => set({ useCustomPalette: !state.useCustomPalette })}
            className="inline-flex items-center gap-1.5 ds-micro-strong text-primary hover:underline"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {state.useCustomPalette
              ? "Use a preset instead"
              : "Build from scratch"}
          </button>
        </div>

        {!state.useCustomPalette ? (
          <div className="grid max-w-[880px] grid-cols-1 gap-3.5 sm:grid-cols-2">
            {PALETTE_PRESETS.slice(0, 3).map((p) => {
              const sel = state.paletteId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => set({ paletteId: p.id, paletteColors: p.colors })}
                  className={cn(
                    "relative rounded-2xl border-2 bg-card p-[18px] text-left transition-all",
                    sel
                      ? "border-primary shadow-[0_8px_32px_rgba(48,50,59,0.06)]"
                      : "border-border hover:border-border/80",
                  )}
                >
                  <div className="mb-3 flex h-16 overflow-hidden rounded-[10px]">
                    {p.colors.map((c, j) => (
                      <div
                        key={j}
                        className="h-full"
                        style={{
                          flex: j === 0 ? 2 : 1,
                          background: c,
                          boxShadow:
                            c.toLowerCase() === "#ffffff"
                              ? "inset 0 0 0 1px rgba(176,177,188,0.4)"
                              : undefined,
                        }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="ds-body-sm-strong text-foreground">{p.name}</div>
                    <div className="flex items-center gap-2.5">
                      <div className="ds-micro ds-mono text-muted-foreground">
                        {p.colors[0]}
                      </div>
                      {sel && <CheckCircle2 className="h-4 w-4 text-primary" />}
                    </div>
                  </div>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => set({ useCustomPalette: true })}
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border bg-muted/20 p-[18px] transition-colors hover:border-primary/50 hover:bg-muted/40"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm text-primary">
                <Plus className="h-6 w-6" />
              </div>
              <div className="ds-body-sm-strong text-foreground">Create custom</div>
            </button>
          </div>
        ) : (
          <CustomPaletteEditor state={state} set={set} />
        )}
      </div>

      <div className="max-w-[880px]">
        <FieldLabel>Typography</FieldLabel>
        <div className="mt-2.5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FontPicker
            label="Heading"
            value={state.headingFontId}
            onChange={(v) => set({ headingFontId: v })}
            preview="Aa"
            big
          />
          <FontPicker
            label="Body"
            value={state.bodyFontId}
            onChange={(v) => set({ bodyFontId: v })}
            preview="The quick brown fox"
          />
        </div>
      </div>
    </>
  );
}

function LogoUploader({
  logoUrl,
  onChange,
}: {
  logoUrl: string | null;
  onChange: (url: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const url = await uploadBrandLogo(file);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Logo upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2.5 flex items-center gap-3">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={cn(
          "relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 transition-colors",
          logoUrl
            ? "border-border bg-foreground"
            : "border-dashed border-border bg-muted/20 hover:border-primary/50 hover:bg-muted/40",
          busy && "opacity-70",
        )}
        aria-label={logoUrl ? "Replace brand logo" : "Upload brand logo"}
      >
        {busy ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : logoUrl ? (
          <img src={logoUrl} alt="" className="h-full w-full object-contain" />
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
            <ImageIcon className="h-5 w-5" />
            <span className="ds-micro-strong uppercase">
              Add
            </span>
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePick}
      />
      <div className="flex flex-col gap-1.5 text-[12px]">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1 font-bold text-foreground hover:bg-muted/40 disabled:opacity-50"
        >
          <ImageIcon className="h-3.5 w-3.5" />
          {logoUrl ? "Replace logo" : "Upload logo"}
        </button>
        {logoUrl && (
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={busy}
            className="inline-flex w-fit items-center gap-1 ds-micro text-muted-foreground hover:text-destructive disabled:opacity-50"
          >
            <X className="h-3 w-3" /> Remove
          </button>
        )}
        {error && <div className="ds-micro text-destructive">{error}</div>}
      </div>
    </div>
  );
}

function CustomPaletteEditor({
  state,
  set,
}: {
  state: FormState;
  set: (patch: Partial<FormState>) => void;
}) {
  const update = (i: number, patch: Partial<ColorEntry>) => {
    const next = state.customColors.map((c, idx) =>
      idx === i ? { ...c, ...patch } : c,
    );
    set({ customColors: next });
  };
  const add = () =>
    set({
      customColors: [...state.customColors, { name: "", hex: "#000000" }],
    });
  const remove = (i: number) =>
    set({ customColors: state.customColors.filter((_, idx) => idx !== i) });

  return (
    <div className="max-w-[880px] rounded-2xl border border-border bg-card p-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {state.customColors.map((color, idx) => (
          <div key={idx} className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-2">
            <input
              type="color"
              className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border border-border bg-transparent p-0.5"
              value={color.hex}
              onChange={(e) => update(idx, { hex: e.target.value })}
            />
            <div className="flex-1 min-w-0">
              <input
                className="w-full bg-transparent text-[13px] font-medium outline-none placeholder:text-muted-foreground"
                value={color.name}
                onChange={(e) => update(idx, { name: e.target.value })}
                placeholder="Role (e.g. Primary)"
              />
              <div className="ds-micro ds-mono text-muted-foreground uppercase">
                {color.hex}
              </div>
            </div>
            {state.customColors.length > 1 && (
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-1.5 text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        className="mt-4 w-full"
      >
        <Plus className="mr-2 h-4 w-4" /> Add custom color
      </Button>
    </div>
  );
}

function FontPicker({
  label,
  value,
  onChange,
  preview,
  big,
}: {
  label: string;
  value: BrandFontId;
  onChange: (v: BrandFontId) => void;
  preview: string;
  big?: boolean;
}) {
  const font = BRAND_TYPOGRAPHY_CATALOG.find((f) => f.id === value) ?? BRAND_TYPOGRAPHY_CATALOG[0];
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <div className="ds-micro-strong uppercase text-muted-foreground">
          {label}
        </div>
      </div>
      <div
        className="flex min-h-[80px] items-center justify-center overflow-hidden rounded-lg bg-muted/20 px-3 py-4 text-foreground"
        style={{
          fontFamily: font.cssFamily,
          fontSize: big ? 42 : 24,
          fontWeight: big ? 900 : 500,
          letterSpacing: big ? "-0.02em" : 0,
        }}
      >
        {preview}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex h-10 w-full items-center justify-between rounded-lg border border-border bg-background px-3 text-[13px] text-foreground outline-none focus:border-primary">
          <span className="truncate">{font.label}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          sideOffset={16}
          className="max-h-[300px] w-56 overflow-y-auto"
        >
          {BRAND_TYPOGRAPHY_CATALOG.map((f) => (
            <DropdownMenuItem
              key={f.id}
              onSelect={(e) => {
                e.preventDefault();
                onChange(f.id);
              }}
              className={cn(
                "flex items-center justify-between",
                value === f.id && "bg-accent text-accent-foreground font-medium"
              )}
              style={{ fontFamily: f.cssFamily }}
            >
              <span className="ds-body truncate">{f.label}</span>
              {value === f.id && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ── Done step ─────────────────────────────────────────────────────────

function StepDone({
  state,
  palette,
  onOpen,
  onEdit,
}: {
  state: FormState;
  palette: string[];
  onOpen: () => void;
  onEdit: () => void;
}) {
  const tones = state.tones
    .map((id) => TONE_CHIPS.find((c) => c.id === id)?.label)
    .filter(Boolean) as string[];
  const headingFamily = BRAND_TYPOGRAPHY_CATALOG.find(
    (f) => f.id === state.headingFontId,
  )?.cssFamily;
  const bodyFamily = BRAND_TYPOGRAPHY_CATALOG.find(
    (f) => f.id === state.bodyFontId,
  )?.cssFamily;
  const headingLabel = BRAND_TYPOGRAPHY_CATALOG.find(
    (f) => f.id === state.headingFontId,
  )?.label;
  const bodyLabel = BRAND_TYPOGRAPHY_CATALOG.find(
    (f) => f.id === state.bodyFontId,
  )?.label;

  return (
    <div>
      <div className="mb-6 flex items-center gap-3.5">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-600">
          <Check className="h-5 w-5" />
        </div>
        <div>
          <div className="ds-micro-strong uppercase text-emerald-600">
            Brand DNA Ready
          </div>
          <h1
            className="mt-1 text-[32px] font-black leading-[1.05] text-foreground"
            style={{ letterSpacing: "-0.025em" }}
          >
            {state.name || "Your brand"} is active in the studio.
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 overflow-hidden rounded-[18px] bg-card shadow-[0_8px_32px_rgba(48,50,59,0.06)] md:grid-cols-[1.2fr_1fr]">
        {/* Identity side */}
        <div className="flex flex-col gap-4 p-7">
          <div className="flex items-center gap-3.5">
            <LivePreview name={state.name} palette={palette} />
            <div>
              <div
                className="text-[22px] font-black text-foreground"
                style={{ letterSpacing: "-0.02em" }}
              >
                {state.name || "New brand"}
              </div>
              <div className="ds-caption text-muted-foreground">
                {state.website
                  ? `https://${state.website.replace(/^https?:\/\//i, "")}`
                  : "—"}
              </div>
            </div>
          </div>

          {state.tagline && (
            <div
              className="rounded-xl bg-muted/40 px-4 py-3.5 ds-title text-foreground"
              style={{
                borderLeft: "3px solid #0053dc",
                letterSpacing: "-0.01em",
              }}
            >
              "{state.tagline}"
            </div>
          )}

          <div>
            <div className="mb-1.5 ds-micro-strong uppercase text-muted-foreground">
              Description
            </div>
            <div className="ds-body-sm-strong text-foreground">
              {state.description || "—"}
            </div>
          </div>

          {tones.length > 0 && (
            <div>
              <div className="mb-2 ds-micro-strong uppercase text-muted-foreground">
                Voice
              </div>
              <div className="flex flex-wrap gap-1.5">
                {tones.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-muted px-2.5 py-1 ds-micro-strong text-foreground"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Visual side */}
        <div className="flex flex-col gap-4 border-l border-border bg-muted/30 p-7">
          <div>
            <div className="mb-2.5 ds-micro-strong uppercase text-muted-foreground">
              Palette
            </div>
            <div className="grid grid-cols-5 gap-2">
              {palette.map((c, i) => (
                <div key={i}>
                  <div
                    className="h-[54px] rounded-lg"
                    style={{
                      background: c,
                      boxShadow:
                        c.toLowerCase() === "#ffffff"
                          ? "inset 0 0 0 1px rgba(176,177,188,0.4)"
                          : undefined,
                    }}
                  />
                  <div className="mt-1 text-center ds-micro ds-mono text-muted-foreground">
                    {c.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2.5 ds-micro-strong uppercase text-muted-foreground">
              Type
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-[10px] border border-border bg-card px-3.5 py-3">
                <div
                  className="text-[28px] font-black leading-none text-foreground"
                  style={{
                    fontFamily: headingFamily,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Aa
                </div>
                <div className="mt-1 ds-micro text-muted-foreground">
                  Heading · {headingLabel}
                </div>
              </div>
              <div className="rounded-[10px] border border-border bg-card px-3.5 py-3">
                <div
                  className="ds-title text-foreground"
                  style={{ fontFamily: bodyFamily }}
                >
                  Aa
                </div>
                <div className="mt-1 ds-micro text-muted-foreground">
                  Body · {bodyLabel}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-4 rounded-[14px] bg-card px-5 py-4 shadow-[0_8px_32px_rgba(48,50,59,0.06)]">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-muted text-primary">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="ds-body-sm-strong text-foreground">
              Next: generate brand assets
            </div>
            <div className="ds-caption text-muted-foreground">
              Or refine industry, audience and competitors on the brand page.
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onEdit}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit DNA
          </Button>
          <Button onClick={onOpen}>
            Open brand <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Footer nav ────────────────────────────────────────────────────────

function FooterNav({
  step,
  canAdvance,
  submitting,
  isLast,
  onBack,
  onNext,
  onSkip,
  onCreate,
}: {
  step: number;
  canAdvance: boolean;
  submitting: boolean;
  isLast: boolean;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  onCreate: () => void;
}) {
  const showSkip = step > 0 && step < TOTAL_INPUT_STEPS - 1;

  return (
    <div className="sticky bottom-0 z-10 flex items-center justify-between border-t border-border bg-background/90 px-7 py-4 backdrop-blur">
      <div>
        {step > 0 && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back
          </Button>
        )}
      </div>
      <div className="flex items-center gap-3">
        {showSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="px-3 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
          >
            Skip for now
          </button>
        )}
        {isLast ? (
          <Button onClick={onCreate} disabled={!canAdvance || submitting}>
            {submitting ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            )}
            Create Brand DNA
          </Button>
        ) : (
          <Button onClick={onNext} disabled={!canAdvance}>
            Continue <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
